import {trackEvent} from '@packages/utilities/analytics';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import type {EventName} from '@packages/shared/segment-typewriter';

export type ClarificationAnswerType = 'option' | 'free_form' | 'canceled';
export type ClarificationRegenerateSource =
  | 'retry_interrupted'
  | 'follow_up_prompt';

export type ClarificationSiteContext = {
  siteId: string;
  workspaceId: string | null;
  userId: string;
};

type ClarificationSession = {
  startedAt: number;
  questionShownAt: number | null;
  answeredAt: number | null;
  promptText: string;
  clarificationQuestion: string | null;
  clarificationOptions: string[];
  answerType: ClarificationAnswerType | null;
  selectedLabel: string | null;
  freeFormAnswer: string | null;
  editedPrompt: boolean;
  componentId: string | null;
  conversationId: string | null;
  /** AIA turn id at answer time; Generation Completed only claims this turn. */
  pendingCreditGroupId: string | null;
  pendingGeneration: boolean;
  generationCompleted: boolean;
  regeneratedFired: boolean;
};

export type ClarificationStartParams = {
  promptText: string;
  componentId?: string | null;
  conversationId?: string | null;
  isRetry?: boolean;
};

export type ClarificationQuestionShownParams = {
  clarificationQuestion: string;
  clarificationOptions: string[];
  componentId?: string | null;
  conversationId?: string | null;
};

export type ClarificationAnsweredParams = {
  answerType: ClarificationAnswerType;
  selectedLabel?: string | null;
  freeFormAnswer?: string | null;
  componentId?: string | null;
  conversationId?: string | null;
  /** AIA credit_group_id / lastUserInputEventId when the answer was submitted. */
  creditGroupId?: string | null;
};

export type ClarificationGenerationCompletedParams = {
  creditGroupId?: string | null;
  componentId?: string | null;
};

export type ClarificationRegeneratedParams = {
  regenerateSource: ClarificationRegenerateSource;
  componentId?: string | null;
  conversationId?: string | null;
};

/**
 * In-memory AICC clarification analytics session. Correlates the fullscreen
 * create-flow funnel and measures time-to-question / answer / generate.
 * One active session at a time (new create clears the previous).
 *
 * Site context is set separately (via setSiteContext) because this singleton
 * calls trackEvent directly and cannot use useTrackEvent's site enrichment.
 */
class AiccClarificationAnalytics {
  private session: ClarificationSession | null = null;
  private siteContext: ClarificationSiteContext | null = null;

  /** Exposed for tests. */
  getSessionForTests(): ClarificationSession | null {
    return this.session;
  }

  reset() {
    this.session = null;
    this.siteContext = null;
  }

  /**
   * Call from the clarification host (CodeComponentApp) so every funnel event
   * carries siteId / workspaceId / userId like other designer VCC events.
   */
  setSiteContext(context: ClarificationSiteContext) {
    this.siteContext = context;
  }

  private track(name: string, data: Record<string, unknown>) {
    const siteData =
      this.siteContext == null
        ? {}
        : {
            siteId: this.siteContext.siteId,
            workspaceId: this.siteContext.workspaceId ?? undefined,
            userId: this.siteContext.userId,
          };

    trackEvent({
      name: name as EventName,
      data: {...siteData, ...data},
    });
  }

  start(params: ClarificationStartParams) {
    const now = Date.now();
    this.session = {
      startedAt: now,
      questionShownAt: null,
      answeredAt: null,
      promptText: params.promptText,
      clarificationQuestion: null,
      clarificationOptions: [],
      answerType: null,
      selectedLabel: null,
      freeFormAnswer: null,
      editedPrompt: false,
      componentId: params.componentId ?? null,
      conversationId: params.conversationId ?? null,
      pendingCreditGroupId: null,
      pendingGeneration: false,
      generationCompleted: false,
      regeneratedFired: false,
    };

    this.track(ANALYTICS_EVENTS.VCC_CLARIFICATION_STARTED, {
      'prompt text': params.promptText,
      componentId: params.componentId ?? undefined,
      conversation_id: params.conversationId ?? undefined,
      'is retry': Boolean(params.isRetry),
    });
  }

  questionShown(params: ClarificationQuestionShownParams) {
    if (!this.session) return;

    // One Question Shown per session (Q1 fullscreen only).
    if (this.session.questionShownAt != null) return;

    const now = Date.now();
    this.session.questionShownAt = now;
    this.session.clarificationQuestion = params.clarificationQuestion;
    this.session.clarificationOptions = params.clarificationOptions;
    if (params.componentId != null) {
      this.session.componentId = params.componentId;
    }
    if (params.conversationId != null) {
      this.session.conversationId = params.conversationId;
    }

    this.track(ANALYTICS_EVENTS.VCC_CLARIFICATION_QUESTION_SHOWN, {
      'prompt text': this.session.promptText,
      'clarification question': params.clarificationQuestion,
      'clarification options': params.clarificationOptions,
      componentId: this.session.componentId ?? undefined,
      conversation_id: this.session.conversationId ?? undefined,
      'time to question ms': now - this.session.startedAt,
    });
  }

  answered(params: ClarificationAnsweredParams) {
    if (!this.session) return;
    if (this.session.answerType != null) return;

    const now = Date.now();
    const editedPrompt = params.answerType === 'free_form';
    this.session.answeredAt = now;
    this.session.answerType = params.answerType;
    this.session.selectedLabel = params.selectedLabel ?? null;
    this.session.freeFormAnswer = params.freeFormAnswer ?? null;
    this.session.editedPrompt = editedPrompt;
    this.session.pendingGeneration = params.answerType !== 'canceled';
    this.session.pendingCreditGroupId =
      params.answerType !== 'canceled' ? (params.creditGroupId ?? null) : null;
    if (params.componentId != null) {
      this.session.componentId = params.componentId;
    }
    if (params.conversationId != null) {
      this.session.conversationId = params.conversationId;
    }

    const timeToAnswerMs =
      this.session.questionShownAt != null
        ? now - this.session.questionShownAt
        : undefined;

    this.track(ANALYTICS_EVENTS.VCC_CLARIFICATION_ANSWERED, {
      'answer type': params.answerType,
      'selected label': params.selectedLabel ?? undefined,
      'free form answer': params.freeFormAnswer ?? undefined,
      'edited prompt': editedPrompt,
      'prompt text': this.session.promptText,
      'clarification question': this.session.clarificationQuestion ?? undefined,
      componentId: this.session.componentId ?? undefined,
      conversation_id: this.session.conversationId ?? undefined,
      'time to answer ms': timeToAnswerMs,
    });
  }

  /**
   * Fires once when the first successful build lands after a non-canceled
   * clarification answer. Returns true if the event fired.
   */
  generationCompleted(
    params: ClarificationGenerationCompletedParams = {}
  ): boolean {
    if (!this.session?.pendingGeneration || this.session.generationCompleted) {
      return false;
    }

    const expectedTurnId = this.session.pendingCreditGroupId;
    if (expectedTurnId != null) {
      // Bound to the answered turn so a later unrelated build cannot claim
      // this session's Generation Completed (stale prompt / inflated timing).
      // Stay armed on mismatch or missing id: lastUserInputEventId can shift
      // mid-turn (e.g. post-tool-response), and an intermediate non-matching
      // build must not permanently drop Generation Completed / Regenerated.
      if (
        params.creditGroupId == null ||
        params.creditGroupId !== expectedTurnId
      ) {
        return false;
      }
    }

    const now = Date.now();
    // Prefer the componentId captured at start(); only fill a missing one.
    if (params.componentId != null && this.session.componentId == null) {
      this.session.componentId = params.componentId;
    }

    const timeToGenerateMs =
      this.session.answeredAt != null
        ? now - this.session.answeredAt
        : undefined;

    this.session.pendingGeneration = false;
    this.session.pendingCreditGroupId = null;
    this.session.generationCompleted = true;

    this.track(ANALYTICS_EVENTS.VCC_CLARIFICATION_GENERATION_COMPLETED, {
      'prompt text': this.session.promptText,
      'clarification question': this.session.clarificationQuestion ?? undefined,
      'selected label': this.session.selectedLabel ?? undefined,
      'free form answer': this.session.freeFormAnswer ?? undefined,
      'edited prompt': this.session.editedPrompt,
      componentId: this.session.componentId ?? undefined,
      conversation_id: this.session.conversationId ?? undefined,
      credit_group_id: params.creditGroupId ?? undefined,
      'time to generate ms': timeToGenerateMs,
    });

    return true;
  }

  /**
   * Fires at most once per clarification session when the user retries or
   * sends a follow-up prompt after generation completed.
   */
  regenerated(params: ClarificationRegeneratedParams): boolean {
    if (!this.session || this.session.regeneratedFired) {
      return false;
    }

    // Follow-up prompts only count after a clarification-led generation.
    if (
      params.regenerateSource === 'follow_up_prompt' &&
      !this.session.generationCompleted
    ) {
      return false;
    }

    this.session.regeneratedFired = true;
    if (params.componentId != null) {
      this.session.componentId = params.componentId;
    }
    if (params.conversationId != null) {
      this.session.conversationId = params.conversationId;
    }

    this.track(ANALYTICS_EVENTS.VCC_CLARIFICATION_REGENERATED, {
      componentId: this.session.componentId ?? undefined,
      conversation_id: this.session.conversationId ?? undefined,
      'regenerate source': params.regenerateSource,
    });

    return true;
  }

  /** True while waiting for the first successful post-answer build. */
  hasPendingGeneration(): boolean {
    return Boolean(this.session?.pendingGeneration);
  }

  /** True after Generation Completed until a new session starts. */
  hasCompletedGeneration(): boolean {
    return Boolean(this.session?.generationCompleted);
  }

  getConversationId(): string | null {
    return this.session?.conversationId ?? null;
  }

  setConversationId(conversationId: string | null) {
    if (this.session && conversationId != null) {
      this.session.conversationId = conversationId;
    }
  }
}

export const aiccClarificationAnalytics = new AiccClarificationAnalytics();
